// src/pages/team/MyTeamsPage.js
import React, { useState, useEffect } from "react";
import { db, auth } from "../../firebase";
import { useAuthState } from "react-firebase-hooks/auth";
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
  deleteDoc,
  updateDoc,
} from "firebase/firestore";
import { useNavigate, Link } from "react-router-dom";

function MyTeamsPage() {
  const [user] = useAuthState(auth);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [fetchError, setFetchError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchMyTeams = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        console.log("Fetching teams for user ID:", user.uid);
        setFetchError(null);
        
        // Query teams where the user is a member
        const memberTeamsQuery = query(
          collection(db, "teams"),
          where("members", "array-contains", user.uid)
        );
        
        console.log("Executing query for teams where user is a member");
        const teamsSnapshot = await getDocs(memberTeamsQuery);
        console.log(`Found ${teamsSnapshot.docs.length} teams`);
        
        // Process the team documents
        const teamsList = teamsSnapshot.docs.map(doc => {
          const data = doc.data();
          console.log(`Team found: ${doc.id}, name: ${data.name}`);
          
          // Determine the user's role in the team
          const isCoach = data.coachId === user.uid;
          
          // Safely check if user is in admins array
          const isAdmin = Array.isArray(data.admins) && data.admins.includes(user.uid);
          
          return {
            id: doc.id,
            ...data,
            role: isCoach ? "coach" : "player",
            isAdmin: isAdmin
          };
        });
        
        // Fetch coach names for teams where user is not the coach
        const coachNamePromises = teamsList
          .filter((team) => team.coachId !== user.uid)
          .map(async (team) => {
            try {
              const coachDoc = await getDoc(doc(db, "users", team.coachId));
              return {
                teamId: team.id,
                coachName: coachDoc.exists()
                  ? coachDoc.data().firstName + " " + coachDoc.data().lastName ||
                    coachDoc.data().email ||
                    "Unknown Coach"
                  : "Unknown Coach",
              };
            } catch (error) {
              console.error("Error fetching coach info:", error);
              return { teamId: team.id, coachName: "Unknown Coach" };
            }
          });

        const coachNames = await Promise.all(coachNamePromises);

        // Update teams with coach names
        const teamsWithCoachNames = teamsList.map((team) => {
          if (team.coachId === user.uid) {
            return { ...team, coachName: "You" };
          }
          const coachInfo = coachNames.find((c) => c.teamId === team.id);
          return { ...team, coachName: coachInfo ? coachInfo.coachName : "Unknown Coach" };
        });

        setTeams(teamsWithCoachNames);
      } catch (err) {
        console.error("Error fetching teams:", err);
        setFetchError(`Failed to load teams: ${err.message}`);
      }

      setLoading(false);
    };

    fetchMyTeams();
  }, [user]);

  const handleTeamClick = (teamId) => {
    navigate(`/team/${teamId}/dashboard`);
  };

  // Function to handle leaving a team
  const handleLeave = async (teamId) => {
    const confirmLeave = window.confirm("Are you sure you want to leave this team?");
    if (!confirmLeave) return;

    try {
      setLoading(true);
      
      // Get the current team data
      const teamRef = doc(db, "teams", teamId);
      const teamDoc = await getDoc(teamRef);
      
      if (!teamDoc.exists()) {
        throw new Error("Team not found");
      }
      
      const teamData = teamDoc.data();
      
      // Check if user is actually a member
      if (!teamData.members || !teamData.members.includes(user.uid)) {
        throw new Error("You are not a member of this team");
      }
      
      // If the user is the coach, they cannot leave
      if (teamData.coachId === user.uid) {
        alert("As the coach, you cannot leave the team. You must either assign a new coach or delete the team.");
        setLoading(false);
        return;
      }
      
      // If the user is an admin, check if they're the only admin
      if (teamData.admins && teamData.admins.includes(user.uid)) {
        const isOnlyAdmin = teamData.admins.length === 1;
        if (isOnlyAdmin) {
          alert("You are the only administrator for this team. Please assign another administrator before leaving.");
          setLoading(false);
          return;
        }
      }
      
      // Remove user from members array
      const updatedMembers = teamData.members.filter(memberId => memberId !== user.uid);
      
      // Remove user from admins array if they're an admin
      let updatedAdmins = teamData.admins;
      if (teamData.admins && teamData.admins.includes(user.uid)) {
        updatedAdmins = teamData.admins.filter(adminId => adminId !== user.uid);
      }
      
      // Update the team document
      await updateDoc(teamRef, {
        members: updatedMembers,
        admins: updatedAdmins
      });
      
      // Also remove the user from any roster entries for this team
      const rosterQuery = query(
        collection(db, "rosters"),
        where("teamId", "==", teamId),
        where("playerEmail", "==", user.email)
      );
      
      const rosterDocs = await getDocs(rosterQuery);
      
      // Delete all matching roster entries
      const deletePromises = rosterDocs.docs.map(doc => deleteDoc(doc.ref));
      await Promise.all(deletePromises);
      
      // Update local state by removing this team
      setTeams(prevTeams => prevTeams.filter(team => team.id !== teamId));
      
      alert("You have successfully left the team.");
    } catch (error) {
      console.error("Error leaving team:", error);
      alert(`Failed to leave the team: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Filter teams based on selected filter and search term
  const filteredTeams = teams.filter((team) => {
    const matchesFilter =
      filter === "all" ||
      (filter === "coach" && team.role === "coach") ||
      (filter === "player" && team.role === "player");

    const matchesSearch =
      team.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      team.coachName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      team.sport?.toLowerCase().includes(searchTerm.toLowerCase());

    return matchesFilter && matchesSearch;
  });

  // Get the sport icon based on sport name
  const getSportIcon = (sport) => {
    if (!sport) return "🏆";
    
    const sportLower = sport.toLowerCase();
    if (sportLower.includes("soccer") || sportLower.includes("football")) return "⚽";
    if (sportLower.includes("basketball")) return "🏀";
    if (sportLower.includes("baseball")) return "⚾";
    if (sportLower.includes("hockey")) return "🏒";
    if (sportLower.includes("volleyball")) return "🏐";
    if (sportLower.includes("tennis")) return "🎾";
    if (sportLower.includes("golf")) return "🏌️";
    if (sportLower.includes("swimming")) return "🏊";
    if (sportLower.includes("running") || sportLower.includes("track")) return "🏃";
    return "🏆";
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading your teams...</p>
        <style jsx>{`
          .loading-container {
            display: flex;
            justify-content: center;
            align-items: center;
            height: 60vh;
            flex-direction: column;
          }
          .spinner {
            border: 4px solid rgba(0, 0, 0, 0.1);
            border-left: 4px solid #1976D2;
            border-radius: 50%;
            width: 40px;
            height: 40px;
            animation: spin 1s linear infinite;
            margin-bottom: 1rem;
          }
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          p {
            color: #555;
            font-size: 1.1rem;
            font-weight: 500;
          }
        `}</style>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="login-container">
        <div className="login-icon">🔒</div>
        <h2>Please Log In</h2>
        <p>You need to be logged in to view and manage your teams.</p>
        <Link to="/login">
          <button className="login-button">Go to Login</button>
        </Link>
        <style jsx>{`
          .login-container {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            height: 60vh;
            text-align: center;
            padding: 2rem;
            background-color: #f9fafb;
            border-radius: 10px;
            max-width: 500px;
            margin: 2rem auto;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
          }
          .login-icon {
            font-size: 4rem;
            margin-bottom: 1.5rem;
            color: #1976D2;
          }
          h2 {
            margin-bottom: 1rem;
            color: #1f2937;
            font-size: 2rem;
            font-weight: 600;
          }
          p {
            color: #4b5563;
            margin-bottom: 2.5rem;
            max-width: 400px;
            line-height: 1.6;
          }
          .login-button {
            background: linear-gradient(45deg, #1976D2, #2196F3);
            color: #fff;
            border: none;
            border-radius: 30px;
            padding: 0.85rem 2.5rem;
            font-size: 1.1rem;
            font-weight: 500;
            cursor: pointer;
            box-shadow: 0 4px 14px rgba(33, 150, 243, 0.3);
            transition: all 0.3s;
          }
          .login-button:hover {
            box-shadow: 0 6px 20px rgba(33, 150, 243, 0.4);
            transform: translateY(-2px);
          }
        `}</style>
      </div>
    );
  }

  return (
    <div className="teams-container">
      <style jsx>{`
        .teams-container {
          max-width: 1200px;
          margin: 3rem auto;
          padding: 0 1.5rem;
          font-family: 'Inter', 'Segoe UI', 'Roboto', sans-serif;
        }
        
        .page-header {
          text-align: center;
          margin-bottom: 2.5rem;
        }
        
        .page-title {
          margin: 0;
          color: #1f2937;
          font-size: 2.5rem;
          font-weight: 700;
          letter-spacing: -0.5px;
        }
        
        .page-subtitle {
          color: #6b7280;
          font-size: 1.1rem;
          margin-top: 0.5rem;
        }
        
        .error-alert {
          padding: 1rem;
          border-radius: 8px;
          margin-bottom: 2rem;
          background-color: #fee2e2;
          color: #b91c1c;
          border-left: 4px solid #ef4444;
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }
        
        .error-icon {
          font-size: 1.5rem;
        }
        
        .error-message {
          font-weight: 500;
        }
        
        .controls-container {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 2rem;
          flex-wrap: wrap;
          gap: 1rem;
        }
        
        .filter-buttons {
          display: flex;
          background-color: #f3f4f6;
          border-radius: 10px;
          padding: 0.3rem;
          overflow: hidden;
        }
        
        .filter-button {
          background: transparent;
          border: none;
          padding: 0.6rem 1.2rem;
          font-size: 0.95rem;
          cursor: pointer;
          border-radius: 8px;
          color: #4b5563;
          font-weight: 500;
          transition: all 0.2s;
        }
        
        .filter-button.active {
          background-color: #fff;
          color: #1976D2;
          box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
        }
        
        .search-container {
          position: relative;
        }
        
        .search-input {
          padding: 0.75rem 1rem 0.75rem 2.75rem;
          border-radius: 10px;
          border: 1px solid #e5e7eb;
          width: 250px;
          font-size: 0.95rem;
          transition: all 0.2s;
          background-color: #f9fafb;
        }
        
        .search-input:focus {
          outline: none;
          border-color: #90caf9;
          box-shadow: 0 0 0 3px rgba(33, 150, 243, 0.2);
          background-color: #fff;
        }
        
        .search-icon {
          position: absolute;
          left: 1rem;
          top: 50%;
          transform: translateY(-50%);
          color: #9ca3af;
          font-size: 1rem;
        }
        
        .team-count {
          font-size: 0.95rem;
          color: #6b7280;
          margin-bottom: 1.5rem;
        }
        
        .empty-state {
          text-align: center;
          padding: 4rem 2rem;
          background-color: #f9fafb;
          border-radius: 12px;
          margin-bottom: 2.5rem;
          border: 1px dashed #d1d5db;
        }
        
        .empty-icon {
          font-size: 4rem;
          margin-bottom: 1.5rem;
          display: inline-block;
        }
        
        .empty-title {
          margin-bottom: 1rem;
          color: #374151;
          font-size: 1.5rem;
          font-weight: 600;
        }
        
        .empty-description {
          color: #6b7280;
          margin-bottom: 2rem;
          max-width: 500px;
          margin-left: auto;
          margin-right: auto;
          line-height: 1.6;
        }
        
        .clear-search {
          background-color: #f3f4f6;
          color: #4b5563;
          border: none;
          border-radius: 8px;
          padding: 0.75rem 1.5rem;
          font-size: 0.95rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
        }
        
        .clear-search:hover {
          background-color: #e5e7eb;
        }
        
        .teams-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
          gap: 1.5rem;
          margin-bottom: 2.5rem;
        }
        
        .team-card {
          background: #ffffff;
          border-radius: 12px;
          overflow: hidden;
          cursor: pointer;
          box-shadow: 0 4px 10px rgba(0, 0, 0, 0.04);
          transition: transform 0.2s, box-shadow 0.2s;
          display: flex;
          flex-direction: column;
          height: 100%;
          border: 1px solid #f3f4f6;
        }
        
        .team-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 12px 20px rgba(0, 0, 0, 0.1);
        }
        
        .team-header {
          padding: 1.5rem;
          border-bottom: 1px solid #f3f4f6;
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
        }
        
        .team-name {
          margin: 0;
          color: #1976D2;
          font-size: 1.4rem;
          font-weight: 700;
          letter-spacing: -0.5px;
          line-height: 1.3;
        }
        
        .role-badge {
          display: inline-block;
          font-size: 0.75rem;
          padding: 0.25rem 0.6rem;
          border-radius: 20px;
          font-weight: 600;
          letter-spacing: 0.5px;
          text-transform: uppercase;
        }
        
        .coach-badge {
          background-color: #e3f2fd;
          color: #0277bd;
        }
        
        .player-badge {
          background-color: #f1f8e9;
          color: #558b2f;
        }
        
        .team-details {
          padding: 1.5rem;
          flex-grow: 1;
        }
        
        .detail-row {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          margin-bottom: 1rem;
          color: #4b5563;
        }
        
        .detail-icon {
          color: #9ca3af;
          font-size: 1.1rem;
          width: 22px;
          text-align: center;
        }
        
        .detail-label {
          font-weight: 500;
        }
        
        .card-actions {
          padding: 1.25rem 1.5rem;
          border-top: 1px solid #f3f4f6;
          background-color: #fafbfc;
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }
        
        .btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          border-radius: 8px;
          padding: 0.85rem 1.25rem;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          width: 100%;
          letter-spacing: 0.3px;
        }
        
        .btn-primary {
          background-color: #1976D2;
          border: none;
          color: white;
          box-shadow: 0 2px 8px rgba(25, 118, 210, 0.3);
        }
        
        .btn-primary:hover {
          background-color: #1565C0;
          box-shadow: 0 4px 12px rgba(25, 118, 210, 0.4);
          transform: translateY(-1px);
        }
        
        .btn-danger {
          background-color: #ef4444;
          border: none;
          color: white;
          box-shadow: 0 2px 8px rgba(239, 68, 68, 0.3);
        }
        
        .btn-danger:hover {
          background-color: #dc2626;
          box-shadow: 0 4px 12px rgba(239, 68, 68, 0.4);
          transform: translateY(-1px);
        }
        
        .action-buttons {
          margin-top: 3rem;
          display: flex;
          justify-content: center;
          flex-wrap: wrap;
          gap: 1rem;
        }
        
        .action-button {
          padding: 1rem 2rem;
          border-radius: 10px;
          border: none;
          font-size: 1.1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: 0 4px 14px rgba(0, 0, 0, 0.15);
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.75rem;
          text-decoration: none;
          min-width: 200px;
        }
        
        .create-button {
          background-color: #1976D2;
          color: #fff;
        }
        
        .create-button:hover {
          background-color: #1565C0;
          box-shadow: 0 8px 20px rgba(25, 118, 210, 0.4);
          transform: translateY(-2px);
        }
        
        .join-button {
          background-color: #43a047;
          color: #fff;
        }
        
        .join-button:hover {
          background-color: #388e3c;
          box-shadow: 0 8px 20px rgba(67, 160, 71, 0.4);
          transform: translateY(-2px);
        }
        
        .delete-button {
          background-color: #e53935;
          color: #fff;
        }
        
        .delete-button:hover {
          background-color: #d32f2f;
          box-shadow: 0 8px 20px rgba(229, 57, 53, 0.4);
          transform: translateY(-2px);
        }
        
        .action-icon {
          font-size: 1.2rem;
        }
        
        @media (max-width: 768px) {
          .controls-container {
            flex-direction: column;
            align-items: stretch;
          }
          
          .filter-buttons, .search-container {
            width: 100%;
          }
          
          .search-input {
            width: 100%;
          }
          
          .teams-grid {
            grid-template-columns: 1fr;
          }
          
          .action-buttons {
            flex-direction: column;
          }
          
          .action-button {
            width: 100%;
            justify-content: center;
          }
        }
      `}</style>

      <div className="page-header">
        <h1 className="page-title">My Teams</h1>
        <p className="page-subtitle">Manage all your teams in one place</p>
      </div>

      {/* Display any fetch errors */}
      {fetchError && (
        <div className="error-alert">
          <span className="error-icon">⚠️</span>
          <span className="error-message">{fetchError}</span>
        </div>
      )}

      {/* Filters and Search */}
      <div className="controls-container">
        <div className="filter-buttons">
          <button
            className={`filter-button ${filter === "all" ? "active" : ""}`}
            onClick={() => setFilter("all")}
          >
            All Teams
          </button>
          <button
            className={`filter-button ${filter === "coach" ? "active" : ""}`}
            onClick={() => setFilter("coach")}
          >
            As Coach
          </button>
          <button
            className={`filter-button ${filter === "player" ? "active" : ""}`}
            onClick={() => setFilter("player")}
          >
            As Player
          </button>
        </div>
        <div className="search-container">
          <input
            type="text"
            className="search-input"
            placeholder="Search teams..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <span className="search-icon">🔍</span>
        </div>
      </div>

      {/* Team Count */}
      <p className="team-count">
        Showing {filteredTeams.length}{" "}
        {filter === "all"
          ? ""
          : filter === "coach"
          ? "coached "
          : "joined "}
        team{filteredTeams.length !== 1 ? "s" : ""}
      </p>

      {/* No Teams Message */}
      {filteredTeams.length === 0 && (
        <div className="empty-state">
          <div className="empty-icon">
            {searchTerm ? "🔍" : filter === "coach" ? "👨‍🏫" : "👥"}
          </div>
          <h3 className="empty-title">
            {searchTerm
              ? "No teams match your search"
              : filter === "all"
              ? "You are not part of any teams yet"
              : filter === "coach"
              ? "You are not coaching any teams yet"
              : "You haven't joined any teams as a player yet"}
          </h3>
          <p className="empty-description">
            {searchTerm
              ? "Try a different search term or clear your search"
              : filter === "all"
              ? "Create a new team or join an existing one to get started"
              : filter === "coach"
              ? "Create a new team to start coaching"
              : "Use a team code to join a team as a player"}
          </p>

          {searchTerm && (
            <button
              className="clear-search"
              onClick={() => setSearchTerm("")}
            >
              Clear Search
            </button>
          )}
        </div>
      )}

      {/* Teams Grid */}
      <div className="teams-grid">
        {filteredTeams.map((team) => (
          <div
            key={team.id}
            className="team-card"
            onClick={() => handleTeamClick(team.id)}
          >
            <div className="team-header">
              <h3 className="team-name">{team.name}</h3>
              <span 
                className={`role-badge ${
                  team.role === "coach" ? "coach-badge" : "player-badge"
                }`}
              >
                {team.role === "coach" ? "Coach" : "Player"}
              </span>
            </div>

            <div className="team-details">
              <div className="detail-row">
                <span className="detail-icon">👤</span>
                <span>
                  <span className="detail-label">Coach:</span> {team.coachName}
                </span>
              </div>

              {team.sport && (
                <div className="detail-row">
                  <span className="detail-icon">{getSportIcon(team.sport)}</span>
                  <span>
                    <span className="detail-label">Sport:</span> {team.sport}
                  </span>
                </div>
              )}

              {team.city && (
                <div className="detail-row">
                  <span className="detail-icon">📍</span>
                  <span>
                    <span className="detail-label">Location:</span> {team.city}
                    {team.stateProvince ? `, ${team.stateProvince}` : ''}
                  </span>
                </div>
              )}

              {team.ageGroup && (
                <div className="detail-row">
                  <span className="detail-icon">👪</span>
                  <span>
                    <span className="detail-label">Age Group:</span> {team.ageGroup}
                  </span>
                </div>
              )}
            </div>

            <div className="card-actions">
              {team.role === "player" && !team.isAdmin && (
                <button
                  className="btn btn-danger"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleLeave(team.id);
                  }}
                >
                  Leave Team
                </button>
              )}
              <button
                className="btn btn-primary"
                onClick={(e) => {
                  e.stopPropagation();
                  handleTeamClick(team.id);
                }}
              >
                View Dashboard
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Action Buttons */}
      <div className="action-buttons">
        <Link to="/create-team" style={{ textDecoration: "none" }}>
          <button className="action-button create-button">
            <span className="action-icon">+</span> Create a Team
          </button>
        </Link>
        <Link to="/join-team" style={{ textDecoration: "none" }}>
          <button className="action-button join-button">
            <span className="action-icon">🤝</span> Join a Team
          </button>
        </Link>
        <Link to="/delete-teams" style={{ textDecoration: "none" }}>
          <button className="action-button delete-button">
            <span className="action-icon">🗑️</span> Delete a Team
          </button>
        </Link>
      </div>
    </div>
  );
}

export default MyTeamsPage;